import type { TableColumnType } from 'antd';
import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * A column that may be hidden when the table is narrow: `drop` 1 goes
 * first, then 2… Columns without `drop` always stay. A column without
 * `width` stretches and needs `minFlex` px to stay readable.
 */
export type FitColumn<T> = TableColumnType<T> & { drop?: number };

/**
 * The columns that fit in `available` px: optional ones are hidden, in
 * their `drop` order, until each stretching column keeps `minFlex` px. Better
 * a hidden column (its content is in the row's panel) than one word a line.
 * `reserve`: the selection column, scroll bar and borders.
 */
export function fitColumns<T>(
  columns: FitColumn<T>[],
  available: number,
  { minFlex = 220, reserve = 24 } = {},
): TableColumnType<T>[] {
  const need = (cols: FitColumn<T>[]) =>
    cols.reduce(
      (sum, c) => sum + (typeof c.width === 'number' ? c.width : minFlex),
      reserve,
    );
  let kept = columns;
  const droppable = columns
    .filter((c) => c.drop !== undefined)
    .sort((a, b) => (a.drop ?? 0) - (b.drop ?? 0));
  for (const c of droppable) {
    if (need(kept) <= available) break;
    kept = kept.filter((k) => k !== c);
  }
  return kept.map(({ drop: _, ...c }) => c);
}

/** The width of an element, followed as it changes (menu folded, panel). */
export function useElementWidth<E extends HTMLElement>() {
  const ref = useRef<E>(null);
  const [width, setWidth] = useState(1200);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const observer = new ResizeObserver(([entry]) =>
      setWidth(entry.contentRect.width),
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);
  return [ref, width] as const;
}

/** `ref` on the table's container, then `fit(columns)`. */
export function useFitColumns<E extends HTMLElement = HTMLElement>() {
  const [ref, width] = useElementWidth<E>();
  const fit = useCallback(
    <T>(columns: FitColumn<T>[], options?: Parameters<typeof fitColumns>[2]) =>
      fitColumns(columns, width, options),
    [width],
  );
  return { ref, fit, width };
}
